import { FC } from 'react';
import { animated, easings, useTransition } from '@react-spring/web';

import { MplIconMessage } from '@mopla/ui';

import type { TBookingInfoIconMessage } from './BookingInfo';
import { SlotWrapper } from './styled';

/** Horizontal travel (px) of the entering/leaving message. */
const SLIDE_OFFSET = 32;

/** Fixed duration, not a spring — a deliberate slide, and one number to tune. */
const SLIDE_CONFIG = { duration: 450, easing: easings.easeInOutCubic };

interface IAnimatedMessageSlotProps {
	message: TBookingInfoIconMessage;
}

/**
 * One message position: a replaced message slides out left, the new one in
 * from the right. `message.key` is the identity, so re-rendered text (a
 * counting-down ETA) stays put; keyless messages never animate.
 */
export const AnimatedMessageSlot: FC<IAnimatedMessageSlotProps> = ({
	message,
}) => {
	const transitions = useTransition([message], {
		keys: (item) => item.key ?? 'static',
		from: { opacity: 0, x: SLIDE_OFFSET },
		enter: { opacity: 1, x: 0 },
		leave: { opacity: 0, x: -SLIDE_OFFSET },
		config: SLIDE_CONFIG,
		/** Skip the entrance on first paint — the sheet already animates open. */
		initial: null,
	});

	return (
		<SlotWrapper>
			{transitions((style, item) => (
				<animated.div style={style}>
					<MplIconMessage
						type={item.type}
						variant={item.variant}
						message={item.message}
					/>
				</animated.div>
			))}
		</SlotWrapper>
	);
};
